import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Download, Loader2, Plus, X } from "lucide-react";
import { Link } from "react-router-dom";

import { Button } from "@/components/ui/Button";
import { Card, CardBody } from "@/components/ui/Card";
import { DateInput, Input } from "@/components/ui/Input";
import {
  AnchoredDetailCalloutBody,
  AnchoredDetailCalloutFieldLabel,
  AnchoredDetailCalloutFooter,
  AnchoredDetailCalloutTitle,
  DEFAULT_FORM_ANCHORED_DETAIL_CALLOUT_WIDTH_PX,
  FormAnchoredDetailCallout,
} from "@/components/ui/AnchoredDetailFormCallout";
import { buildAttributeAnchorSelector } from "@/components/ui/AnchoredDetailCallout";
import { SortableTableHeader, type SortDirection } from "@/components/ui/SortableTableHeader";
import { PageTitleWithHelp } from "@/components/ui/title-help";
import { ApiError, apiFetch } from "@/lib/api";
import { buildInsideSalesSalesOrderEditPath } from "@/lib/hubEntityDetailPaths";
import { useIntegratorMe } from "@/lib/me-query";
import { EM_DASH } from "@/lib/utils";

import {
  coerceReportFilterOperator,
  compareInsideSalesSalesOrderReportRows,
  createInsideSalesReportColumnFilter,
  downloadInsideSalesSalesOrderReportCsv,
  filterInsideSalesSalesOrderReportRows,
  formatInsideSalesReportColumnFilterChip,
  formatReportDate,
  insideSalesReportSalesOrderNumberClassName,
  INSIDE_SALES_REPORT_FILTER_COLUMNS,
  operatorsForReportFilterColumn,
  reportColumnFilterIsActive,
  reportFilterColumnKind,
  type InsideSalesReportColumnFilter,
  type InsideSalesReportFilterOperator,
  type InsideSalesSalesOrderReportRow,
  type InsideSalesSalesOrderReportSortKey,
} from "./insideSalesSalesOrderReport";
import {
  DEFAULT_INSIDE_SALES_REPORT_FILTER_SNAPSHOT,
  readInsideSalesReportFilterSnapshot,
  writeInsideSalesReportFilterSnapshot,
} from "./insideSalesSalesOrderReportFilterStorage";

const ADD_FILTER_ANCHOR = '[data-sales-order-report-filter-anchor="add"]';

function chipAnchorSelector(id: string): string {
  return buildAttributeAnchorSelector("data-sales-order-report-filter-chip", id);
}

type FilterEditor = {
  mode: "create" | "edit";
  draft: InsideSalesReportColumnFilter;
};

const filterControlClass =
  "h-8 w-full rounded-md border border-slate-300 bg-white px-2 text-sm text-slate-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500";

type ReportResponse = {
  rows: InsideSalesSalesOrderReportRow[];
  truncated: boolean;
  mineOwnerMissing?: boolean;
};

const moneyFormatter = new Intl.NumberFormat(undefined, {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function FilterValueInputs({
  filter,
  onChange,
}: {
  filter: InsideSalesReportColumnFilter;
  onChange: (patch: Partial<InsideSalesReportColumnFilter>) => void;
}) {
  const kind = reportFilterColumnKind(filter.column);
  const operator = coerceReportFilterOperator(filter.column, filter.operator);
  const between = operator === "between";
  const useDate = kind === "date" && operator !== "contains" && operator !== "not_contains";
  const inputId = `sales-order-report-filter-value-${filter.id}`;
  const toId = `sales-order-report-filter-value-to-${filter.id}`;

  if (useDate) {
    return (
      <div className="flex min-w-0 items-center gap-2">
        <DateInput
          id={inputId}
          value={filter.value}
          onChange={(event) => onChange({ value: event.target.value })}
          className="h-8 px-2"
          aria-label={between ? "From date" : "Value"}
        />
        {between ? (
          <>
            <span className="shrink-0 text-xs text-slate-500">and</span>
            <DateInput
              id={toId}
              value={filter.valueTo}
              onChange={(event) => onChange({ valueTo: event.target.value })}
              className="h-8 px-2"
              aria-label="To date"
            />
          </>
        ) : null}
      </div>
    );
  }

  return (
    <div className="flex min-w-0 items-center gap-2">
      <Input
        id={inputId}
        type={kind === "number" ? "number" : "text"}
        step={kind === "number" ? "any" : undefined}
        value={filter.value}
        onChange={(event) => onChange({ value: event.target.value })}
        placeholder={between ? "From" : "Value"}
        className="h-8 px-2"
        aria-label={between ? "From value" : "Value"}
      />
      {between ? (
        <>
          <span className="shrink-0 text-xs text-slate-500">and</span>
          <Input
            id={toId}
            type={kind === "number" ? "number" : "text"}
            step={kind === "number" ? "any" : undefined}
            value={filter.valueTo}
            onChange={(event) => onChange({ valueTo: event.target.value })}
            placeholder="To"
            className="h-8 px-2"
            aria-label="To value"
          />
        </>
      ) : null}
    </div>
  );
}

export function InsideSalesReportsPage() {
  const meQuery = useIntegratorMe();
  const userId = meQuery.data?.user.id ?? null;
  const [search, setSearch] = useState(DEFAULT_INSIDE_SALES_REPORT_FILTER_SNAPSHOT.search);
  const [mine, setMine] = useState(DEFAULT_INSIDE_SALES_REPORT_FILTER_SNAPSHOT.mine);
  const [columnFilters, setColumnFilters] = useState<InsideSalesReportColumnFilter[]>(
    DEFAULT_INSIDE_SALES_REPORT_FILTER_SNAPSHOT.columnFilters,
  );
  const [editor, setEditor] = useState<FilterEditor | null>(null);
  const [sortKey, setSortKey] = useState<InsideSalesSalesOrderReportSortKey>(
    DEFAULT_INSIDE_SALES_REPORT_FILTER_SNAPSHOT.sortKey,
  );
  const [sortDir, setSortDir] = useState<SortDirection>(DEFAULT_INSIDE_SALES_REPORT_FILTER_SNAPSHOT.sortDir);
  const [filtersHydratedFor, setFiltersHydratedFor] = useState<string | null>(null);

  useEffect(() => {
    if (userId == null) {
      setFiltersHydratedFor(null);
      return;
    }
    const saved = readInsideSalesReportFilterSnapshot(userId);
    if (saved != null) {
      setSearch(saved.search);
      setMine(saved.mine);
      setColumnFilters(saved.columnFilters);
      setSortKey(saved.sortKey);
      setSortDir(saved.sortDir);
    } else {
      setSearch(DEFAULT_INSIDE_SALES_REPORT_FILTER_SNAPSHOT.search);
      setMine(DEFAULT_INSIDE_SALES_REPORT_FILTER_SNAPSHOT.mine);
      setColumnFilters(DEFAULT_INSIDE_SALES_REPORT_FILTER_SNAPSHOT.columnFilters);
      setSortKey(DEFAULT_INSIDE_SALES_REPORT_FILTER_SNAPSHOT.sortKey);
      setSortDir(DEFAULT_INSIDE_SALES_REPORT_FILTER_SNAPSHOT.sortDir);
    }
    setFiltersHydratedFor(userId);
  }, [userId]);

  useEffect(() => {
    if (filtersHydratedFor !== userId || userId == null) {
      return;
    }
    writeInsideSalesReportFilterSnapshot(userId, {
      search,
      mine,
      columnFilters,
      sortKey,
      sortDir,
    });
  }, [columnFilters, filtersHydratedFor, mine, search, sortDir, sortKey, userId]);

  const reportQuery = useQuery({
    queryKey: ["inside-sales", "sales-order-report", { mine }],
    queryFn: () =>
      apiFetch<ReportResponse>(`/api/inside-sales/sales-order-report${mine ? "?mine=1" : ""}`),
  });

  const rows = useMemo(() => {
    const filtered = filterInsideSalesSalesOrderReportRows(
      reportQuery.data?.rows ?? [],
      search,
      columnFilters,
    );
    return [...filtered].sort((left, right) =>
      compareInsideSalesSalesOrderReportRows(left, right, sortKey, sortDir),
    );
  }, [columnFilters, reportQuery.data?.rows, search, sortDir, sortKey]);

  function patchDraft(patch: Partial<InsideSalesReportColumnFilter>): void {
    setEditor((current) => {
      if (current == null) {
        return current;
      }
      const next: InsideSalesReportColumnFilter = { ...current.draft, ...patch };
      const previousKind = reportFilterColumnKind(current.draft.column);
      const nextKind = reportFilterColumnKind(next.column);
      next.operator = coerceReportFilterOperator(next.column, next.operator);
      if (previousKind !== nextKind) {
        next.value = "";
        next.valueTo = "";
      }
      if (next.operator !== "between") {
        next.valueTo = "";
      }
      return { ...current, draft: next };
    });
  }

  function onSort(key: InsideSalesSalesOrderReportSortKey): void {
    if (key === sortKey) {
      setSortDir((current) => (current === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(key);
    setSortDir(
      key === "submitted" || key === "shipByDate" || key === "quantity" || key === "unitPrice" || key === "lineValue"
        ? "desc"
        : "asc",
    );
  }

  const loading = reportQuery.isPending;
  const errorMessage =
    reportQuery.error instanceof ApiError
      ? reportQuery.error.message
      : reportQuery.error instanceof Error
        ? reportQuery.error.message
        : reportQuery.isError
          ? "Could not load the sales order report."
          : null;
  const columnFiltersActive = columnFilters.some(reportColumnFilterIsActive);
  const filtersActive = search.trim() !== "" || mine || columnFiltersActive;
  const emptyMessage = reportQuery.data?.mineOwnerMissing
    ? "Your hub user is not linked to a Freshsales owner, so Your accounts has nothing to match."
    : filtersActive
      ? "No sales order lines match these filters."
      : "No sales order lines.";

  function resetFilters(): void {
    setSearch("");
    setMine(false);
    setColumnFilters([]);
    setEditor(null);
  }

  function openCreateFilter(): void {
    if (editor?.mode === "create") {
      setEditor(null);
      return;
    }
    setEditor({ mode: "create", draft: createInsideSalesReportColumnFilter() });
  }

  function applyEditor(): void {
    if (editor == null || !reportColumnFilterIsActive(editor.draft)) {
      return;
    }
    const applied = editor.draft;
    setColumnFilters((current) => {
      if (editor.mode === "create") {
        return [...current, applied];
      }
      return current.map((filter) => (filter.id === applied.id ? applied : filter));
    });
    setEditor(null);
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      <PageTitleWithHelp
        className="shrink-0"
        hoverHint="Line-item sales order report. Newest SO dates first. Cancelled orders are omitted. Search matches any of SO, PO, client, end user, model, rep, or owner. Use Add filter for Equal, Not equal, Contain, Not contain, Before, After, Between, Less than, or Greater than. Applied filters and sort stay in this browser until you press Reset. Download saves the lines currently in the table. Owner is the end-user Freshsales owner’s staff name. Your accounts keeps lines whose end-user Freshsales owner matches the signed-in hub user. SO numbers are amber while Pending and green after that."
      >
        Reports
      </PageTitleWithHelp>

      <Card className="shrink-0">
        <CardBody className="flex flex-wrap items-center gap-2 px-3 py-2">
          <Input
            id="sales-order-report-search"
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search GSO, PO, client, end user, model, rep, or owner..."
            aria-label="Search"
            className="h-8 w-72 min-w-[12rem] shrink-0 px-2"
          />
          {columnFilters.map((filter) => {
            const label = formatInsideSalesReportColumnFilterChip(filter);
            return (
              <span
                key={filter.id}
                data-sales-order-report-filter-chip={filter.id}
                className="inline-flex max-w-full items-center rounded-full border border-slate-200 bg-slate-50 text-xs text-slate-700"
              >
                <button
                  type="button"
                  className="max-w-[16rem] truncate py-1 pl-2.5 pr-1 font-medium"
                  title={label}
                  onClick={() => setEditor({ mode: "edit", draft: { ...filter } })}
                >
                  {label}
                </button>
                <button
                  type="button"
                  className="rounded-full px-1.5 py-1 text-slate-500 hover:bg-slate-200 hover:text-slate-800"
                  aria-label={`Remove ${label}`}
                  onClick={() => {
                    setColumnFilters((current) => current.filter((entry) => entry.id !== filter.id));
                    if (editor?.draft.id === filter.id) {
                      setEditor(null);
                    }
                  }}
                >
                  <X className="h-3.5 w-3.5" aria-hidden />
                </button>
              </span>
            );
          })}
          <Button
            type="button"
            size="sm"
            variant={mine ? "primary" : "secondary"}
            aria-pressed={mine}
            title="Show lines whose end-user Freshsales owner matches the signed-in hub user."
            onClick={() => setMine((current) => !current)}
          >
            Your accounts
          </Button>
          <Button
            type="button"
            size="sm"
            variant={editor?.mode === "create" ? "primary" : "secondary"}
            data-sales-order-report-filter-anchor="add"
            aria-expanded={editor?.mode === "create"}
            onClick={openCreateFilter}
          >
            <Plus className="h-4 w-4" aria-hidden />
            Add filter
          </Button>
          <Button type="button" size="sm" variant="ghost" disabled={!filtersActive} onClick={resetFilters}>
            Reset
          </Button>
          <div className="ml-auto flex shrink-0 items-center gap-2">
            <p className="text-sm tabular-nums text-slate-600" aria-live="polite">
              {loading
                ? "Loading…"
                : `${rows.length.toLocaleString()} lines${
                    reportQuery.data && rows.length !== reportQuery.data.rows.length
                      ? ` of ${reportQuery.data.rows.length.toLocaleString()}`
                      : ""
                  }`}
            </p>
            <Button
              type="button"
              size="sm"
              variant="secondary"
              disabled={rows.length === 0 || loading}
              onClick={() => downloadInsideSalesSalesOrderReportCsv(rows)}
            >
              <Download className="h-4 w-4" aria-hidden />
              Download
            </Button>
          </div>
        </CardBody>
      </Card>
      {editor != null ? (
        <FormAnchoredDetailCallout
          anchorSelector={editor.mode === "edit" ? chipAnchorSelector(editor.draft.id) : ADD_FILTER_ANCHOR}
          onClose={() => setEditor(null)}
          panelWidth={DEFAULT_FORM_ANCHORED_DETAIL_CALLOUT_WIDTH_PX}
          minContentHeight={220}
          ariaLabel={editor.mode === "edit" ? "Edit filter" : "Add filter"}
          measureKey={`${editor.mode}-${editor.draft.id}-${editor.draft.column}-${editor.draft.operator}`}
        >
          <AnchoredDetailCalloutBody>
            <AnchoredDetailCalloutTitle>{editor.mode === "edit" ? "Edit filter" : "Add filter"}</AnchoredDetailCalloutTitle>
            <AnchoredDetailCalloutFieldLabel label="Column" htmlFor="sales-order-report-filter-column">
              <select
                id="sales-order-report-filter-column"
                value={editor.draft.column}
                onChange={(event) =>
                  patchDraft({ column: event.target.value as InsideSalesSalesOrderReportSortKey })
                }
                className={filterControlClass}
              >
                {INSIDE_SALES_REPORT_FILTER_COLUMNS.map((column) => (
                  <option key={column.key} value={column.key}>
                    {column.label}
                  </option>
                ))}
              </select>
            </AnchoredDetailCalloutFieldLabel>
            <AnchoredDetailCalloutFieldLabel label="Operator" htmlFor="sales-order-report-filter-operator">
              <select
                id="sales-order-report-filter-operator"
                value={coerceReportFilterOperator(editor.draft.column, editor.draft.operator)}
                onChange={(event) =>
                  patchDraft({ operator: event.target.value as InsideSalesReportFilterOperator })
                }
                className={filterControlClass}
              >
                {operatorsForReportFilterColumn(editor.draft.column).map((option) => (
                  <option key={option.key} value={option.key}>
                    {option.label}
                  </option>
                ))}
              </select>
            </AnchoredDetailCalloutFieldLabel>
            <AnchoredDetailCalloutFieldLabel label="Value">
              <FilterValueInputs filter={editor.draft} onChange={patchDraft} />
            </AnchoredDetailCalloutFieldLabel>
          </AnchoredDetailCalloutBody>
          <AnchoredDetailCalloutFooter>
            <Button type="button" size="sm" variant="secondary" onClick={() => setEditor(null)}>
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={!reportColumnFilterIsActive(editor.draft)}
              onClick={applyEditor}
            >
              Apply
            </Button>
          </AnchoredDetailCalloutFooter>
        </FormAnchoredDetailCallout>
      ) : null}

      <Card className="flex min-h-0 flex-1 flex-col">
        <CardBody className="min-h-0 flex-1 overflow-auto p-0">
          {loading ? (
            <div className="flex min-h-[12rem] flex-col items-center justify-center gap-2 p-5 text-sm text-slate-600">
              <Loader2 className="h-6 w-6 animate-spin text-slate-500" aria-hidden />
              Loading sales order lines…
            </div>
          ) : null}
          {errorMessage ? <div className="p-5 text-sm text-red-700">{errorMessage}</div> : null}
          {!errorMessage && !loading && rows.length === 0 ? (
            <div className="p-5 text-sm text-slate-500">{emptyMessage}</div>
          ) : null}
          {rows.length > 0 ? (
            <table className="w-full min-w-[80rem] table-fixed border-collapse text-sm">
              <colgroup>
                <col className="w-[7.25rem]" />
                <col className="w-[8rem]" />
                <col />
                <col />
                <col className="w-[6.5rem]" />
                <col className="w-[6.5rem]" />
                <col className="w-[11rem]" />
                <col className="w-[5.5rem]" />
                <col className="w-[6.75rem]" />
                <col className="w-[6.75rem]" />
                <col />
                <col className="w-[7.5rem]" />
                <col className="w-[10rem]" />
              </colgroup>
              <thead className="sticky top-0 z-10 border-b border-slate-200 bg-slate-50/95 backdrop-blur-sm">
                <tr>
                  <SortableTableHeader
                    label="SO #"
                    sortKey="number"
                    activeKey={sortKey}
                    dir={sortDir}
                    onSort={onSort}
                    className="whitespace-nowrap px-3"
                  />
                  <SortableTableHeader
                    label="PO #"
                    sortKey="customerPurchaseOrderNumber"
                    activeKey={sortKey}
                    dir={sortDir}
                    onSort={onSort}
                    className="whitespace-nowrap px-3"
                  />
                  <SortableTableHeader
                    label="Client"
                    sortKey="clientName"
                    activeKey={sortKey}
                    dir={sortDir}
                    onSort={onSort}
                    className="px-3"
                  />
                  <SortableTableHeader
                    label="End user"
                    sortKey="endUserName"
                    activeKey={sortKey}
                    dir={sortDir}
                    onSort={onSort}
                    className="px-3"
                  />
                  <SortableTableHeader
                    label="SO date"
                    sortKey="submitted"
                    activeKey={sortKey}
                    dir={sortDir}
                    onSort={onSort}
                    className="whitespace-nowrap px-3"
                  />
                  <SortableTableHeader
                    label="Ship date"
                    sortKey="shipByDate"
                    activeKey={sortKey}
                    dir={sortDir}
                    onSort={onSort}
                    className="whitespace-nowrap px-3"
                  />
                  <SortableTableHeader
                    label="Model"
                    sortKey="model"
                    activeKey={sortKey}
                    dir={sortDir}
                    onSort={onSort}
                    className="px-3"
                  />
                  <SortableTableHeader
                    label="Qty"
                    sortKey="quantity"
                    activeKey={sortKey}
                    dir={sortDir}
                    onSort={onSort}
                    align="right"
                    className="whitespace-nowrap px-3"
                  />
                  <SortableTableHeader
                    label="Unit price"
                    sortKey="unitPrice"
                    activeKey={sortKey}
                    dir={sortDir}
                    onSort={onSort}
                    align="right"
                    className="whitespace-nowrap px-3"
                  />
                  <SortableTableHeader
                    label="Value"
                    sortKey="lineValue"
                    activeKey={sortKey}
                    dir={sortDir}
                    onSort={onSort}
                    align="right"
                    className="whitespace-nowrap px-3"
                  />
                  <SortableTableHeader
                    label="Rep agency"
                    sortKey="repAgencyName"
                    activeKey={sortKey}
                    dir={sortDir}
                    onSort={onSort}
                    className="px-3"
                  />
                  <SortableTableHeader
                    label="Ship state"
                    sortKey="shippingState"
                    activeKey={sortKey}
                    dir={sortDir}
                    onSort={onSort}
                    className="whitespace-nowrap px-3"
                  />
                  <SortableTableHeader
                    label="Owner"
                    sortKey="ownerName"
                    activeKey={sortKey}
                    dir={sortDir}
                    onSort={onSort}
                    className="px-3"
                  />
                </tr>
              </thead>
              <tbody>
                {rows.map((line) => (
                  <tr key={line.id} className="border-b border-slate-100 hover:bg-slate-50/80 hover:[&>td]:bg-slate-50/80">
                    <td className="truncate px-3 py-2 whitespace-nowrap">
                      <Link
                        to={buildInsideSalesSalesOrderEditPath(line.number)}
                        className={insideSalesReportSalesOrderNumberClassName(line.status)}
                        title={line.number}
                      >
                        {line.number}
                      </Link>
                    </td>
                    <td className="truncate px-3 py-2 whitespace-nowrap text-slate-700" title={line.customerPurchaseOrderNumber ?? undefined}>
                      {line.customerPurchaseOrderNumber ?? EM_DASH}
                    </td>
                    <td className="max-w-0 truncate px-3 py-2 text-slate-900" title={line.clientName ?? undefined}>
                      {line.clientName ?? EM_DASH}
                    </td>
                    <td className="max-w-0 truncate px-3 py-2 text-slate-700" title={line.endUserName ?? undefined}>
                      {line.endUserName ?? EM_DASH}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-slate-700">{formatReportDate(line.submitted) ?? EM_DASH}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-slate-700">{formatReportDate(line.shipByDate) ?? EM_DASH}</td>
                    <td className="truncate px-3 py-2 text-slate-900" title={line.model ?? undefined}>
                      {line.model ?? EM_DASH}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap text-slate-700">{line.quantity}</td>
                    <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap text-slate-700">
                      {moneyFormatter.format(line.unitPrice)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap text-slate-900">
                      {moneyFormatter.format(line.lineValue)}
                    </td>
                    <td className="max-w-0 truncate px-3 py-2 text-slate-700" title={line.repAgencyName ?? undefined}>
                      {line.repAgencyName ?? EM_DASH}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-slate-700">{line.shippingState ?? EM_DASH}</td>
                    <td className="max-w-0 truncate px-3 py-2 text-slate-700" title={line.ownerName ?? undefined}>
                      {line.ownerName ?? EM_DASH}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}
        </CardBody>
      </Card>
    </div>
  );
}
